import { useRef, useEffect, useState, memo, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { containerElement, setViewerContainer } from 'Domain/Panel/Handler/ContainerManager'
import { Control } from 'Domain/Panel/Model/OrbitControl'

import { Header } from 'Ui/Header'
import { Tools } from 'Ui/Layer/Tools'
import { Informations } from 'Ui/Layer/Information'
import { Player } from 'Ui/Layer/Player'
import { Live } from 'Ui/Layer/Live'
import { Movements } from 'Ui/Layer/Movements'
import { ModalContainer } from 'Ui/Components/ModalContainer'
import { Notification } from './Layer/Notification'
import { ColorRange } from 'Ui/Components/ColorRange'
import { DentalsDisplay } from 'Ui/Components/DentalsDisplay'
import { ActionsPanel } from 'Ui/Layer/components/information/ActionsPanel'
import { PatientResumeCompact } from 'Ui/Layer/components/information/PatientResumeCompact'
import { getSkullIcon } from 'Ui/Layer/components/icons/ToolsIcons'
import { useTools } from 'Ui/Layer/hooks/useTools'
import { useInformation } from 'Ui/Layer/hooks/useInformation'
import 'Application/Templates/Css/MobilePortrait.css'

const MobileToolbar = memo(({ onEditMode, setOnEditMode, changeActive, mainViewerLayer, toolbarInfo, dispatch }) => (
    <div className="portrait-button-scroll">
        <ColorRange />
        <DentalsDisplay onEditMode={onEditMode} />
        <button className="portrait-btn toolbar-btn" onClick={() => changeActive('all')}>
            {getSkullIcon('all', mainViewerLayer)}
        </button>
        <button className="portrait-btn toolbar-btn" onClick={() => changeActive('maxi')}>
            {getSkullIcon('maxi', mainViewerLayer)}
        </button>
        <button className="portrait-btn toolbar-btn" onClick={() => changeActive('mandi')}>
            {getSkullIcon('mandi', mainViewerLayer)}
        </button>

        <div className="portrait-actions-bar">
            <ActionsPanel
                selectedVersion={toolbarInfo.selectedVersion}
                viewportMode={toolbarInfo.viewportMode}
                onEditMode={onEditMode}
                passive={false}
                hasChanges={toolbarInfo.hasChanges}
                handleSaveAndExit={toolbarInfo.handleSaveAndExit}
                dispatch={dispatch}
                setOnEditMode={setOnEditMode}
                deniedServices={toolbarInfo.deniedServices}
                versions={toolbarInfo.versions}
                currentValue={toolbarInfo.currentValue}
                handleVersionChange={toolbarInfo.handleVersionChange}
                handleEnterEdition={toolbarInfo.handleEnterEdition}
                logoUrl={toolbarInfo.logoUrl}
            />
        </div>
    </div>
))

export const Main = props => {
    const { isPortrait } = props;
    const [onEditMode, setOnEditMode] = useState(false);
    const mount = useRef(null);
    const main = useRef(null);
    const dispatch = useDispatch();
    const { ViewportReducer, AnimationReducer, LayoutReducer, UserReducer } = useSelector((state) => state);

    const gridLoaded = useSelector(state => state.ViewportReducer.gridLoaded);

    const {
        viewerType,
        mainViewerLayer,
        changeActive,
    } = useTools({ onEditMode, setOnEditMode });

    const info = useInformation({ passive: false, onEditMode, setOnEditMode, actions: props.project.actions });

    const toolbarInfo = useMemo(() => ({
        selectedVersion: info.selectedVersion,
        viewportMode: info.viewportMode,
        hasChanges: info.hasChanges,
        handleSaveAndExit: info.handleSaveAndExit,
        deniedServices: info.deniedServices,
        versions: info.versions,
        currentValue: info.currentValue,
        handleVersionChange: info.handleVersionChange,
        handleEnterEdition: info.handleEnterEdition,
        logoUrl: info.logoUrl,
    }), [
        info.selectedVersion,
        info.viewportMode,
        info.hasChanges,
        info.handleSaveAndExit,
        info.deniedServices,
        info.versions,
        info.currentValue,
        info.handleVersionChange,
        info.handleEnterEdition,
        info.logoUrl,
    ]);

    const disposeRef = useRef(() => {})

    useEffect(() => {
        containerElement(mount.current, { project: props.project }).then(fn => {
            disposeRef.current = fn || (() => {})
        })

        const requestNotificationPermission = () => {
            if (typeof window !== 'undefined' && 'Notification' in window) {
                if (Notification.permission === 'default') {
                    Notification.requestPermission().then(permission => {
                        console.log('[Notification] Permission state changed:', permission);
                    });
                } else if (Notification.permission === 'denied') {
                    alert("Attention : Vous avez bloqué les notifications. Cliquez sur le cadenas dans la barre d'adresse pour les autoriser afin de recevoir les nouveaux messages.");
                }
            }
        };
        document.addEventListener('click', requestNotificationPermission, { once: true });

        return () => {
            const dispose = disposeRef.current
            if (dispose) dispose()
            document.removeEventListener('click', requestNotificationPermission);
        }
    }, [props.project])

    useEffect(() => {
        if (mount.current) {
            setViewerContainer(mount.current)
        }
    }, [isPortrait])

    useEffect(() => {
        if (gridLoaded) {
            Control.setOrientationZoom(isPortrait)
        }
    }, [isPortrait, gridLoaded])

    const isAdvancedViewer = viewerType !== 'simple';
    const shouldShowLive = UserReducer.connected && !UserReducer.expired && UserReducer.hasLiveAccess;

    const isMobileLandscape = useSelector(state => state.LayoutReducer.global?.isMobileLandscape ?? false)

    if (isPortrait) {
        const shouldShowCompact = !info.deniedServices.includes('titleBlock') && !!info.metas?.caseData;
        const isEditMode = ViewportReducer.mode === 'edition';
        const shouldShowMobileToolbar = isAdvancedViewer;

        return (
            <main ref={main} className="portrait-layout">
                <div className="portrait-top-panel glass-panel">
                    <Header project={props.project} isPortrait={true} />

                    {shouldShowCompact && (
                        <div className="portrait-case-data">
                            <PatientResumeCompact
                                metas={info.metas}
                                isConnected={info.isConnected}
                                getAnonymous={info.getAnonymous}
                                className={isEditMode ? 'edition-visible' : ''}
                            />
                        </div>
                    )}

                    {shouldShowMobileToolbar && <MobileToolbar onEditMode={onEditMode} setOnEditMode={setOnEditMode} changeActive={changeActive} mainViewerLayer={mainViewerLayer} toolbarInfo={toolbarInfo} dispatch={dispatch} />}
                </div>

                <section className="portrait-viewer-area">
                    <article ref={mount} id='viewer' style={{ opacity: gridLoaded ? 1 : 0, transition: 'opacity 0.5s' }}/>

                    {shouldShowLive && LayoutReducer.live?.open ? (
                        <div id='live' className="mobile-live">
                            <Live
                                caseId={props.project.caseId + '~' + props.project.store}
                                tab={LayoutReducer.live?.tab || 'chat'}
                            />
                        </div>
                    ) : null}

                    <Notification
                        caseId={props.project.caseId + '~' + props.project.store}
                        type={{ dz: false, chat: false }}
                        connected={UserReducer.connected && !UserReducer.expired}
                    />
                </section>

                <ModalContainer />
            </main>
        );
    }

    const shouldShowLandscapeMobileToolbar = isMobileLandscape && isAdvancedViewer;
    const isSingleOrComparative = ViewportReducer.viewportMode === 'single' || ViewportReducer.viewportMode === 'comparative';
    const shouldShowDesktopTools = !isMobileLandscape && isSingleOrComparative;

    return (
        <main ref={main} className={isMobileLandscape ? 'mobile-landscape' : undefined}>
            <Informations actions={props.project.actions} passive={false} onEditMode={onEditMode} setOnEditMode={setOnEditMode} times={AnimationReducer.times}/>

            {shouldShowLandscapeMobileToolbar && (
                <nav id="RightPanel" className="mobile-landscape-right-panel">
                    <div className="mobile-landscape-toolbar">
                        <MobileToolbar onEditMode={onEditMode} setOnEditMode={setOnEditMode} changeActive={changeActive} mainViewerLayer={mainViewerLayer} toolbarInfo={toolbarInfo} dispatch={dispatch} />
                    </div>
                </nav>
            )}

            <section>
                <article ref={mount} id='viewer' style={{ opacity: gridLoaded ? 1 : 0, transition: 'opacity 0.5s' }}/>
                <Player times={AnimationReducer.times} treatmentPlanning={AnimationReducer.metas.treatmentPlanning}/>
                <span id="silverLogo" className="desktop">
                    <img src={process.env.REACT_APP_U1_VIEWERSVC_APP + "img/logo-C4W-light.svg"} alt=""/>
                </span>
                {shouldShowLive && LayoutReducer.live?.open ? (
                    <div id='live' className={isMobileLandscape ? 'mobile-live' : undefined}>
                        <Live
                            caseId={props.project.caseId + '~' + props.project.store}
                            tab={LayoutReducer.live?.tab || 'chat'}
                        />
                    </div>
                ) : null}
                <Notification
                    caseId={props.project.caseId + '~' + props.project.store}
                    type={{ dz: false, chat: false }}
                    connected={UserReducer.connected && !UserReducer.expired}
                />
            </section>

            {shouldShowDesktopTools ? (
                <Tools onEditMode={onEditMode} setOnEditMode={setOnEditMode} />
            ) : null}
            {onEditMode ? <Movements /> : null}
            <ModalContainer />
        </main>
    )
}
